class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.updateViewport = this.updateViewport.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    window.addEventListener('resize', this.updateViewport);
    window.visualViewport?.addEventListener('resize', this.updateViewport);
    window.visualViewport?.addEventListener('scroll', this.updateViewport);
    if (window.ResizeObserver) this.previewBarResizeObserver = new ResizeObserver(this.updateViewport);
    this.previewBarMutationObserver = new MutationObserver(() => {
      this.observePreviewBar();
      this.updateViewport();
    });
    this.previewBarMutationObserver.observe(document.body, { childList: true });
    this.observePreviewBar();
    this.updateViewport();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateViewport);
    window.visualViewport?.removeEventListener('resize', this.updateViewport);
    window.visualViewport?.removeEventListener('scroll', this.updateViewport);
    this.previewBarResizeObserver?.disconnect();
    this.previewBarMutationObserver?.disconnect();
    this.previewBar = null;
  }

  observePreviewBar() {
    const previewBar = document.getElementById('PBarNextFrameWrapper');
    if (previewBar === this.previewBar) return;
    this.previewBarResizeObserver?.disconnect();
    this.previewBar = previewBar;
    if (previewBar) this.previewBarResizeObserver?.observe(previewBar);
  }

  updateViewport() {
    const viewport = window.visualViewport;
    const top = viewport?.offsetTop || 0;
    let bottom = top + (viewport?.height || window.innerHeight);
    const previewBarBounds = this.previewBar?.getBoundingClientRect();
    if (
      previewBarBounds?.height > 0 &&
      previewBarBounds.width > 0 &&
      previewBarBounds.top >= top &&
      previewBarBounds.top < bottom &&
      previewBarBounds.bottom >= bottom - 1
    ) {
      bottom = previewBarBounds.top;
    }
    this.style.setProperty('--cart-drawer-top', `${top}px`);
    this.style.setProperty('--cart-drawer-height', `${Math.max(0, bottom - top)}px`);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.observePreviewBar();
    this.updateViewport();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
